import React from 'react';
import { trackError } from '../utils/errorTracking';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      errorId: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Actualizar el estado para que el siguiente render muestre la UI de fallback
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Generar un ID único para este error
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    this.setState({
      error,
      errorInfo,
      errorId
    });

    // Log del error en GA4 con contexto detallado
    const errorContext = {
      errorId,
      componentStack: errorInfo.componentStack,
      userAgent: navigator.userAgent,
      url: window.location.href,
      timestamp: new Date().toISOString(),
      errorMessage: error.message,
      errorName: error.name,
      errorStack: error.stack,
      errorType: 'react_error_boundary',
      page: window.location.pathname,
      referrer: document.referrer
    };

    // Enviar a GA4
    trackError(error, errorContext);

    // También log en consola para desarrollo
    console.error('Error Boundary caught an error:', error, errorInfo);
    console.error('Error ID:', errorId);
  }

  handleReload = () => {
    window.location.reload();
  };

  handleReportError = () => {
    // Aquí podrías implementar un formulario para que el usuario reporte el error
    // Por ahora, solo mostramos la información en consola
    console.log('Error details for reporting:', {
      errorId: this.state.errorId,
      error: this.state.error,
      errorInfo: this.state.errorInfo
    });
    
    // También podrías abrir un modal o redirigir a una página de reporte
    alert(`Por favor, reporta este error con el ID: ${this.state.errorId}`);
  };

  render() {
    if (this.state.hasError) {
      // UI de fallback personalizada
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="mb-6">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h1 className="text-xl font-semibold text-gray-900 mb-2">
                Algo salió mal
              </h1>
              <p className="text-gray-600 mb-4">
                Ha ocurrido un error inesperado. Nuestro equipo ha sido notificado automáticamente.
              </p>
              
              {this.state.errorId && (
                <div className="bg-gray-100 rounded-md p-3 mb-4">
                  <p className="text-sm text-gray-700">
                    <strong>ID del Error:</strong> {this.state.errorId}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Guarda este ID para reportar el problema
                  </p>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <button
                onClick={this.handleReload}
                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
              >
                Recargar página
              </button>
              
              <button
                onClick={this.handleReportError}
                className="w-full bg-gray-200 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-300 transition-colors"
              >
                Reportar error
              </button>
            </div>

            {/* Información adicional para desarrolladores en desarrollo */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-gray-600 hover:text-gray-800">
                  Detalles del error (solo desarrollo)
                </summary>
                <div className="mt-2 p-3 bg-red-50 rounded-md text-xs font-mono text-red-800 overflow-auto max-h-40">
                  <div><strong>Error:</strong> {this.state.error.toString()}</div>
                  <div className="mt-2"><strong>Stack:</strong></div>
                  <pre className="whitespace-pre-wrap">{this.state.error.stack}</pre>
                  <div className="mt-2"><strong>Component Stack:</strong></div>
                  <pre className="whitespace-pre-wrap">{this.state.errorInfo.componentStack}</pre>
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 